/**
 * PositionsPage.js — Phase 4B: "Pozitat & Qasjet" (Nexuma HR).
 *
 * Minimal admin UI to make positions + position permissions usable:
 *   - list / create / edit / delete positions
 *   - module × action permission matrix (per position)
 *   - link a worker to a position (dropdown)
 *   - "Lejet e mia" preview (current user's effective permissions)
 *
 * Frontend-only. Uses the existing Phase 4A endpoints via apiService():
 *   GET /api/modules · /api/positions · /api/positions/:id · /api/me/permissions
 *   POST/PUT/DELETE /api/positions · PUT /api/positions/:id/permissions
 *   PUT /api/workers/:id/position
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, List, ListItemButton, ListItemText, TextField, Button,
  Table, TableHead, TableBody, TableRow, TableCell, Switch, Tooltip,
  Select, MenuItem, FormControl, InputLabel, Snackbar, Alert, Chip, Stack, Avatar, FormControlLabel,
  Collapse, IconButton,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockIcon from '@mui/icons-material/Lock';
import BadgeIcon from '@mui/icons-material/Badge';
import SaveIcon from '@mui/icons-material/Save';
import SecurityIcon from '@mui/icons-material/Security';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import apiService from '../services/apiServices';

// 7 canonical actions (mirror of backend config/modules.js ACTIONS)
const ACTIONS = [
  { key: 'view', label: 'Shiko' }, { key: 'create', label: 'Krijo' },
  { key: 'edit', label: 'Ndrysho' }, { key: 'delete', label: 'Fshi' },
  { key: 'approve', label: 'Aprovo' }, { key: 'export', label: 'Eksporto' },
  { key: 'manage', label: 'Menaxho' },
];
// Modules that can never be granted via a position (mirror of backend ADMIN_ONLY_MODULES)
const ADMIN_ONLY = new Set(['settings', 'users', 'roles', 'audit', 'data_io']);

const cell = (m, a) => `${m}.${a}`;

/**
 * Sa larg vlen e drejta e pozitës.
 *
 * Deri tani lejet e pozitës ruheshin pa fushëveprim, kurse rrugët e kërkojnë
 * (`leave.view.company`) — ndaj matrica dukej se jepte qasje dhe serveri kthente
 * 403. Tani zgjidhet për çdo modul: një «Team Lead» sheh ekipin, një «HR
 * Manager» tërë kompaninë, me të njëjtën kuti të zgjedhur.
 */
const SCOPES = [
  { key: 'own',        label: 'Veta' },
  { key: 'team',       label: 'Ekipi' },
  { key: 'department', label: 'Departamenti' },
  { key: 'company',    label: 'Kompania' },
];
const DEFAULT_SCOPE = 'department';
const api = () => apiService();

export default function PositionsPage({ embedded = false }) {
  const [modules, setModules]       = useState([]);
  const [positions, setPositions]   = useState([]);
  const [workers, setWorkers]       = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm]             = useState({ name: '', description: '', c_level: false });
  const [permSet, setPermSet]       = useState(() => new Set());
  const [scopeByModule, setScopeByModule] = useState(() => ({}));
  const [assignWorker, setAssignWorker] = useState('');
  const [myPerms, setMyPerms]       = useState(null);
  const [myPermsOpen, setMyPermsOpen] = useState(false);
  const [toast, setToast]           = useState({ open: false, msg: '', sev: 'success' });

  const notify = (msg, sev = 'success') => setToast({ open: true, msg, sev });
  const theme  = useTheme();
  const ACCENT = theme.palette.primary.main;

  // ── loaders ────────────────────────────────────────────────────────────────
  const loadPositions = useCallback(async () => {
    try { const r = await api().get('positions'); setPositions(r?.data || []); }
    catch { notify('Nuk u ngarkuan pozitat', 'error'); }
  }, []);

  const loadModules = useCallback(async () => {
    try { const r = await api().get('modules'); setModules(r?.data || []); } catch { /* noop */ }
  }, []);

  const loadWorkers = useCallback(async () => {
    try {
      const r = await api().get('workers');
      const list = r?.data || r || [];
      setWorkers(Array.isArray(list) ? list : []);
    } catch { /* noop */ }
  }, []);

  const loadMyPerms = useCallback(async () => {
    try { const r = await api().get('me/permissions'); setMyPerms(r?.data || null); } catch { /* noop */ }
  }, []);

  useEffect(() => { loadModules(); loadPositions(); loadWorkers(); loadMyPerms(); }, [loadModules, loadPositions, loadWorkers, loadMyPerms]);

  // ── select a position → load its detail + permissions ────────────────────────
  const selectPosition = useCallback(async (id) => {
    setSelectedId(id);
    try {
      const r = await api().getById('positions', id);
      const p = r?.data || {};
      setForm({ name: p.name || '', description: p.description || '', c_level: p.c_level === true });
      setPermSet(new Set((p.permissions || []).map(x => cell(x.module, x.action))));
      const scopes = {};
      for (const x of p.permissions || []) if (x.scope) scopes[x.module] = x.scope;
      setScopeByModule(scopes);
    } catch { notify('Nuk u ngarkua pozita', 'error'); }
  }, []);

  // ── create / update / delete position ────────────────────────────────────────
  const newPosition = () => { setSelectedId(null); setForm({ name: '', description: '', c_level: false }); setPermSet(new Set()); setScopeByModule({}); };

  const saveInfo = async () => {
    if (!form.name.trim()) return notify('Emri kërkohet', 'error');
    try {
      if (selectedId) {
        await api().put('positions', { id: selectedId, name: form.name, description: form.description, c_level: !!form.c_level });
        notify('Pozita u përditësua');
      } else {
        const r = await api().post('positions', { name: form.name, description: form.description, c_level: !!form.c_level });
        const id = r?.data?.id;
        // Matrica e sapombushur ruhet PARA se të rilexohet pozita: `selectPosition`
        // e zëvendëson me atë të serverit (bosh), ndaj deri tani kutitë e zgjedhura
        // zhdukeshin pa asnjë paralajmërim sapo klikohej «Krijo pozitë».
        const pending = Array.from(permSet).map((k) => { const [module, action] = k.split('.'); return { module, action, scope: scopeByModule[module] || DEFAULT_SCOPE }; });
        if (id && pending.length) {
          try { await api().put(`positions/${id}/permissions`, { permissions: pending }); }
          catch (e) { notify(e?.message || 'Pozita u krijua, por lejet nuk u ruajtën', 'error'); }
        }
        await loadPositions();
        if (id) await selectPosition(id);
        notify(pending.length ? `Pozita u krijua me ${pending.length} të drejta` : 'Pozita u krijua');
        return;
      }
      await loadPositions();
    } catch (e) { notify(e?.message || 'Gabim gjatë ruajtjes', 'error'); }
  };

  const removePosition = async () => {
    if (!selectedId) return;
    try {
      await api().delete('positions', selectedId);
      notify('Pozita u fshi');
      newPosition();
      await loadPositions();
    } catch (e) { notify(e?.message || 'Gabim gjatë fshirjes', 'error'); }
  };

  // ── matrix toggling + save ───────────────────────────────────────────────────
  const togglePerm = (m, a) => {
    if (ADMIN_ONLY.has(m)) return; // hard cap
    setPermSet(prev => {
      const next = new Set(prev);
      const k = cell(m, a);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };

  const savePermissions = async () => {
    if (!selectedId) return notify('Zgjidh ose krijo një pozitë së pari', 'error');
    const permissions = Array.from(permSet).map(k => {
      const [module, action] = k.split('.');
      return { module, action, scope: scopeByModule[module] || DEFAULT_SCOPE };
    });
    try {
      const r = await api().put(`positions/${selectedId}/permissions`, { permissions });
      notify(`U ruajtën ${r?.data?.count ?? permissions.length} të drejta`);
    } catch (e) { notify(e?.message || 'Gabim gjatë ruajtjes së lejeve', 'error'); }
  };

  // ── link worker → position ───────────────────────────────────────────────────
  const linkWorker = async () => {
    if (!selectedId) return notify('Zgjidh një pozitë së pari', 'error');
    if (!assignWorker)  return notify('Zgjidh një punëtor', 'error');
    try {
      await api().put(`workers/${assignWorker}/position`, { position_id: selectedId });
      notify('Punëtori u lidh me pozitën');
      setAssignWorker('');
      await loadMyPerms();
    } catch (e) { notify(e?.message || 'Gabim gjatë lidhjes', 'error'); }
  };

  // /api/workers vjen në PascalCase (workerFromDb: FirstName/LastName/Email) — provoji të gjitha format.
  const workerName = (w) =>
    `${w.first_name || w.firstName || w.Name || w.FirstName || ''} ${w.last_name || w.lastName || w.Surname || w.LastName || ''}`.trim()
    || w.email || w.Email || `#${w.id || w.Id}`;

  /**
   * Ky është një kontroll i shpejtë për veten, jo përmbajtja e faqes.
   *
   * Lista dilte e shpalosur gjithmonë: te një admin me tetëdhjetë leje, footer-i
   * bëhej mur me tekst me pika — në telefon zinte gjithë ekranin dhe e shtynte
   * poshtë punën e vërtetë. Tani jep numrin, dhe listën e hap kush e do.
   */
  const myPermsInfo = useMemo(() => {
    if (!myPerms) return { summary: 'Po ngarkohet…', list: null, count: 0 };
    // /api/me/permissions kthen { wildcard, permissions:[...] }; wildcard → '*'.
    if (myPerms.wildcard || (Array.isArray(myPerms.permissions) && myPerms.permissions.includes('*'))) {
      return { summary: 'Të gjitha të drejtat (qasje e plotë)', list: null, count: 0 };
    }
    const list = Array.isArray(myPerms.permissions) ? myPerms.permissions : [];
    if (!list.length) return { summary: 'Asnjë e drejtë efektive', list: null, count: 0 };
    return { summary: `${list.length} leje`, list: list.join(', '), count: list.length };
  }, [myPerms]);

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
      {!embedded && (
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>Pozitat & Qasjet</Typography>
      )}

      {/* Header row — konsistent me tab-in Përdoruesit */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }} flexWrap="wrap" gap={1}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <BadgeIcon sx={{ fontSize: 20, color: ACCENT }} />
          <Typography variant="subtitle1" fontWeight={700}>Pozitat e punës</Typography>
          <Chip label={`${positions.length} pozita`} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 600 }} />
        </Stack>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={newPosition}
          sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}>
          Krijo pozitë
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Krijo pozita, cakto çfarë lejohet në çdo modul dhe lidh punëtorët. Lejet e pozitës <strong>i shtohen</strong> rolit të secilit.
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px 1fr' }, gap: 2, alignItems: 'start' }}>
        {/* ── Lista e pozitave ── */}
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 1.75, py: 1.1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
              Lista e pozitave
            </Typography>
          </Box>
          <List dense disablePadding sx={{ maxHeight: 520, overflowY: 'auto' }}>
            {positions.map(p => {
              const sel = p.id === selectedId;
              return (
                <ListItemButton key={p.id} selected={sel} onClick={() => selectPosition(p.id)}
                  sx={{
                    py: 1, borderLeft: '3px solid', borderColor: sel ? ACCENT : 'transparent',
                    '&.Mui-selected': { bgcolor: alpha(ACCENT, 0.08) },
                    '&.Mui-selected:hover': { bgcolor: alpha(ACCENT, 0.12) },
                  }}>
                  <Avatar sx={{ width: 30, height: 30, mr: 1.25, fontSize: 12, fontWeight: 700,
                    bgcolor: sel ? ACCENT : 'grey.200', color: sel ? '#fff' : 'text.secondary' }}>
                    {(p.name || '?').slice(0, 2).toUpperCase()}
                  </Avatar>
                  <ListItemText
                    primary={(
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="body2" fontWeight={sel ? 700 : 600} noWrap>{p.name}</Typography>
                        {p.c_level === true && <Chip size="small" label="C-Level" sx={{ height: 18, fontSize: '.62rem', fontWeight: 800, bgcolor: '#FEF3C7', color: '#92400E' }} />}
                      </Stack>
                    )}
                    secondary={p.description ? <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{p.description}</Typography> : null}
                  />
                </ListItemButton>
              );
            })}
            {!positions.length && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <WorkOutlineIcon sx={{ fontSize: 30, color: 'text.disabled', mb: 0.5 }} />
                <Typography variant="body2" color="text.secondary">Asnjë pozitë ende.</Typography>
                <Typography variant="caption" color="text.secondary">Kliko "Krijo pozitë" për të filluar.</Typography>
              </Box>
            )}
          </List>
        </Paper>

        {/* ── Detajet e pozitës ── */}
        <Stack spacing={2}>
          {/* Info */}
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <BadgeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle2" fontWeight={700}>{selectedId ? 'Ndrysho pozitën' : 'Pozitë e re'}</Typography>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField label="Emri i pozitës" size="small" fullWidth value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              <TextField label="Përshkrimi" size="small" fullWidth value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </Stack>
            {/* C-Level: rritja e pagës për këtë pozitë e anashkalon fazën e HR-së
                te «Karriera & Kompensimi» (mbikëqyrësi → menaxhmenti). Vendimin
                e zbaton serveri në parashtrim; këtu vetëm vendoset flamuri. */}
            <FormControlLabel
              sx={{ mt: 1, ml: 0, alignItems: 'flex-start' }}
              control={<Switch size="small" checked={!!form.c_level} onChange={e => setForm(f => ({ ...f, c_level: e.target.checked }))} />}
              label={(
                <Box sx={{ ml: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>Pozitë C-Level (menaxhment i lartë)</Typography>
                  <Typography variant="caption" color="text.secondary">Kërkesat për ndryshim page të këtij punonjësi kalojnë pa fazën e HR-së: mbikëqyrësi → menaxhmenti.</Typography>
                </Box>
              )}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={saveInfo}
                sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}>
                {selectedId ? 'Ruaj' : 'Krijo pozitën'}
              </Button>
              {selectedId && (
                <Button color="error" size="small" variant="outlined" startIcon={<DeleteOutlineIcon />} onClick={removePosition}
                  sx={{ borderRadius: 1.5, textTransform: 'none' }}>Fshi</Button>
              )}
            </Stack>
          </Paper>

          {/* Matrica e të drejtave */}
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }} flexWrap="wrap" gap={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <SecurityIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="subtitle2" fontWeight={700}>Të drejtat sipas modulit</Typography>
                {selectedId && (
                  <Chip size="small" label={`${permSet.size} aktive`}
                    color={permSet.size ? 'success' : 'default'} variant={permSet.size ? 'filled' : 'outlined'}
                    sx={{ height: 20, fontSize: 10, fontWeight: 600 }} />
                )}
              </Stack>
              <Button variant="contained" size="small" startIcon={<SaveIcon />} disabled={!selectedId} onClick={savePermissions}
                sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}>
                Ruaj të drejtat
              </Button>
            </Stack>
            {!selectedId ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <InfoOutlinedIcon sx={{ fontSize: 26, color: 'text.disabled', mb: 0.5 }} />
                <Typography variant="body2" color="text.secondary">Zgjidh ose krijo një pozitë për të caktuar të drejtat.</Typography>
              </Box>
            ) : (
              <Box sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Moduli</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11, minWidth: 130 }}>
                        Sa larg vlen
                        <Tooltip title="Deri ku shtrihet e drejta: vetëm te vetja, ekipi, departamenti, ose tërë kompania.">
                          <InfoOutlinedIcon sx={{ fontSize: 13, ml: 0.5, color: 'text.disabled', verticalAlign: 'middle' }} />
                        </Tooltip>
                      </TableCell>
                      {ACTIONS.map(a => <TableCell key={a.key} align="center" sx={{ fontWeight: 700, fontSize: 11 }}>{a.label}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {modules.map(m => {
                      const locked = ADMIN_ONLY.has(m.key);
                      return (
                        <TableRow key={m.key} hover sx={{ opacity: locked ? 0.5 : 1 }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                            {m.label}
                            {locked && (
                              <Tooltip title="Vetëm admin — s'mund t'i jepet pozitës">
                                <LockIcon sx={{ fontSize: 13, ml: 0.5, color: 'text.disabled', verticalAlign: 'middle' }} />
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 0.25 }}>
                            <Select size="small" disabled={locked}
                              value={scopeByModule[m.key] || DEFAULT_SCOPE}
                              onChange={(e) => setScopeByModule(prev => ({ ...prev, [m.key]: e.target.value }))}
                              sx={{ fontSize: 12, '& .MuiSelect-select': { py: 0.4 } }}>
                              {SCOPES.map(sc => <MenuItem key={sc.key} value={sc.key} sx={{ fontSize: 12 }}>{sc.label}</MenuItem>)}
                            </Select>
                          </TableCell>
                          {ACTIONS.map(a => {
                            const supported = m.actions.includes(a.key);
                            if (!supported) return <TableCell key={a.key} align="center" sx={{ color: 'text.disabled' }}>·</TableCell>;
                            return (
                              <TableCell key={a.key} align="center" sx={{ p: 0.25 }}>
                                <Switch size="small" disabled={locked}
                                  checked={permSet.has(cell(m.key, a.key))}
                                  onChange={() => togglePerm(m.key, a.key)} />
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>

          {/* Lidhja punëtor → pozitë */}
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <PersonAddAltIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle2" fontWeight={700}>Lidh një punëtor me këtë pozitë</Typography>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <FormControl size="small" sx={{ flex: 1, minWidth: 240 }} disabled={!selectedId}>
                <InputLabel>Punëtori</InputLabel>
                <Select label="Punëtori" value={assignWorker} onChange={e => setAssignWorker(e.target.value)}>
                  {workers.map(w => (
                    <MenuItem key={w.id || w.Id} value={w.id || w.Id}>{workerName(w)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" size="small" disabled={!selectedId || !assignWorker} onClick={linkWorker}
                sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}>Cakto pozitën</Button>
            </Stack>
            {!selectedId && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Zgjidh një pozitë së pari.</Typography>}
          </Paper>
        </Stack>
      </Box>

      {/* Lejet e mia — footer i vogël (self-check), i palosur derisa të kërkohet */}
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.25, mt: 2, bgcolor: 'grey.50' }}>
        <Box
          {...(myPermsInfo.list ? {
            role: 'button',
            onClick: () => setMyPermsOpen(v => !v),
            sx: { display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', cursor: 'pointer' },
          } : { sx: { display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' } })}
        >
          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" fontWeight={700} color="text.secondary">Lejet e mia efektive:</Typography>
          {myPerms?.role && <Chip size="small" label={myPerms.role} sx={{ height: 20, fontSize: 10 }} />}
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 120 }}>{myPermsInfo.summary}</Typography>
          {myPermsInfo.list && (
            <IconButton size="small" sx={{ p: 0.25 }} aria-label={myPermsOpen ? 'Fshih listën' : 'Shfaq listën'}>
              <ExpandMoreIcon sx={{ fontSize: 18, transform: myPermsOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
            </IconButton>
          )}
        </Box>
        {myPermsInfo.list && (
          <Collapse in={myPermsOpen}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, overflowWrap: 'anywhere' }}>
              {myPermsInfo.list}
            </Typography>
          </Collapse>
        )}
      </Paper>

      <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.sev} variant="filled" onClose={() => setToast(t => ({ ...t, open: false }))}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
